import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApplicationPackage } from 'src/app/enums/application-package.enum';
import { Service } from 'src/app/enums/service.enum';
import { DailyPrograms } from 'src/app/model/cms-btv-core/daily-programs.model';
import { Genre } from 'src/app/model/cms-btv-core/genre.model';
import { PageOfProgramMinimized } from 'src/app/model/cms-btv-core/page-of-program-minimized.model';
import { PageOfProgram } from 'src/app/model/cms-btv-core/page-of-program-model';
import { Program } from 'src/app/model/cms-btv-core/program-model';
import { ManualEpgImportResult } from 'src/app/model/cms-btv-core/manual-epg-import-result.model';
import { ProgramP } from 'src/app/model/cms-btv-core/programP.model';
import { environment } from 'src/environments/environment';
import { CustomHttpUrlEncodingCodec } from '../../../main/encoder';
import { QueryParams } from '../../../model/query-models/query-models';
import { ProgramPoster } from 'src/app/model/cms-btv-core/program-controller/program-poster.model';
import { EpgCorrectionJob } from 'src/app/model/epg-correction-job.model';
import { ProgramInputPath } from 'src/app/model/cms-btv-core/program-input-path.model';

@Injectable({
  providedIn: 'root',
})
export class ProgramService {
  readonly programApiUrl = environment.endPoint + ApplicationPackage.CMS_BTV + Service.PROGRAM;
  readonly channelGenreApiUrl = environment.endPoint + ApplicationPackage.CMS_BTV + Service.GENRE;

  private activeTabSubject = new BehaviorSubject<string>('ALL');
  activeTab$ = this.activeTabSubject.asObservable();

  constructor(private http: HttpClient) { }

  updateActiveTab(value: string) {
    this.activeTabSubject.next(value);
  }

  getAllGenre(): Observable<Genre[]> {
    return this.http.get<Genre[]>(this.channelGenreApiUrl);
  }

  //Get all programs
  getAllProgramStream(programParams: QueryParams): Observable<PageOfProgram> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    programParams.page = programParams.page - 1;
    if (programParams.epgProgramId) {
      queryParams = queryParams.set('epgProgramId', <any>programParams.epgProgramId);
    }
    if (programParams.programTitle) {
      queryParams = queryParams.set('title', <any>programParams.programTitle);
    }
    if (programParams.startTime) {
      queryParams = queryParams.set('startTime', <any>programParams.startTime);
    }
    if (programParams.endTime) {
      queryParams = queryParams.set('endTime', <any>programParams.endTime);
    }
    if (programParams.page > 0) {
      queryParams = queryParams.set('page', <any>programParams.page);
    }
    if (programParams.sortParam) {
      queryParams = queryParams.set('sortParam', <any>programParams.sortParam);
    }
    if (programParams.itemCount) {
      queryParams = queryParams.set('itemCount', <any>programParams.itemCount);
    }
    if (programParams.channelId) {
      queryParams = queryParams.set('channelId', <any>programParams.channelId);
    }
    if (programParams.releaseDate) {
      queryParams = queryParams.set('releaseDate', <any>programParams.releaseDate);
    }
    return this.http.get<PageOfProgram>(this.programApiUrl, { params: queryParams });
  }

  getProgramWithoutBanner(programParams: QueryParams): Observable<PageOfProgram> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    programParams.page = programParams.page - 1;
    if (programParams.epgProgramId) {
      queryParams = queryParams.set('epgProgramId', <any>programParams.epgProgramId);
    }
    if (programParams.programTitle) {
      queryParams = queryParams.set('title', <any>programParams.programTitle);
    }
    if (programParams.startTime) {
      queryParams = queryParams.set('startTime', <any>programParams.startTime);
    }
    if (programParams.endTime) {
      queryParams = queryParams.set('endTime', <any>programParams.endTime);
    }
    if (programParams.page > 0) {
      queryParams = queryParams.set('page', <any>programParams.page);
    }
    if (programParams.sortParam) {
      queryParams = queryParams.set('sortParam', <any>programParams.sortParam);
    }
    if (programParams.itemCount) {
      queryParams = queryParams.set('itemCount', <any>programParams.itemCount);
    }
    if (programParams.channelId) {
      queryParams = queryParams.set('channelId', <any>programParams.channelId);
    }
    if (programParams.releaseDate) {
      queryParams = queryParams.set('releaseDate', <any>programParams.releaseDate);
    }
    if (programParams.justValid) {
      queryParams = queryParams.set('justValid', <any>programParams.justValid);
    }

    return this.http.get<PageOfProgram>(this.programApiUrl + '/getProgramWithoutBanner', { params: queryParams });
  }


  getAllDailyProgramStream(): Observable<DailyPrograms[]> {
    return this.http.get<DailyPrograms[]>(this.programApiUrl + '/daily');
  }

  getProgramDetail(epgProgramId: number): Observable<ProgramP> {
    return this.http.get<ProgramP>(this.programApiUrl + '/' + epgProgramId);
  }
  // Get Program by  epg id
  getProgramById(epgProgramId: string): Observable<Program> {
    return this.http.get<Program>(this.programApiUrl + `/${epgProgramId}`);
  }

  getProgramByIds(epgProgramIds: string[]): Observable<Program[]> {
    return this.http.post<Program[]>(this.programApiUrl + '/getAll', epgProgramIds);
  }

  setDetectionRange(epgProgramId: string, detectionStartTime: number, detectionEndTime: number): Observable<any> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (epgProgramId) {
      queryParams = queryParams.set('epgProgramId', <any>epgProgramId);
    }
    if (detectionStartTime) {
      queryParams = queryParams.set('detectionStartTime', <any>detectionStartTime);
    }
    if (detectionEndTime) {
      queryParams = queryParams.set('detectionEndTime', <any>detectionEndTime);
    }
    return this.http.put<number[]>(this.programApiUrl + `/setdetectionrange`, epgProgramId, { params: queryParams, responseType: 'text' as 'json' });
  }
  /** Program Bulk Edit Request */
  setProgramBulkEdit(programs: Program[]): Observable<string> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.put(this.programApiUrl + `/bulk`, programs, { headers, responseType: 'text' });
  }

  setProgramBulkDelete(programs: Program[]): Observable<string> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.put(this.programApiUrl + `/bulk/delete`, programs, { headers, responseType: 'text' });
  }

  startBulkJob(program: Program[]): Observable<Program[]> {
    const headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');
    return this.http.post<Program[]>(this.programApiUrl + `/bulk/startepgjob`, program, {
      headers,
      responseType: 'text' as 'json',
    });
  }
  captureScreenshot2(epgProgramId: string, formData: FormData): Observable<string> {
    return this.http.post<string>(
      `${this.programApiUrl}/screenshot2/${epgProgramId}`,
      formData,
      { responseType: 'text' as 'json' }
    );
  }

  captureScreenshot(epgProgramId: string, streamAddress: string, frameSecond: number): Observable<string> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (streamAddress) {
      queryParams = queryParams.set('streamAddress', <any>streamAddress);
    }
    if (frameSecond) {
      queryParams = queryParams.set('frameSecond', <any>frameSecond);
    }
    return this.http.post<string>(this.programApiUrl + '/screenshot/' + epgProgramId, null, { params: queryParams, responseType: 'text' as 'json' });
  }

  /** Program Update */
  updateProgram(program: Program): Observable<string> {
    return this.http.put(this.programApiUrl, program, { responseType: 'text' });
  }

  publishProgram(epgProgramId: string): Observable<Program> {
    return this.http.post<Program>(this.programApiUrl + `/${epgProgramId}/publish`, null, { responseType: 'text' as 'json' });
  }
  unpublishProgram(epgProgramId: string): Observable<Program> {
    return this.http.post<Program>(this.programApiUrl + `/${epgProgramId}/unpublish`, null, { responseType: 'text' as 'json' });
  }

  updateProgramPoster(epgProgramId: string, file: File, type: string, autoPosterCreation: boolean): Observable<Program> {
    let formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    queryParams = queryParams.set('autoPosterCreate', <any>autoPosterCreation);
    return this.http.post<Program>(this.programApiUrl + `/${epgProgramId}/posters/upload`, formData, {
      params: queryParams,
      responseType: 'text' as 'json',
    });
  }


  updateProgramPoster2(epgProgramId: string, posterDefinitionId: number, autoPosterCreate: boolean, file: File): Observable<Program> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (autoPosterCreate) {
      queryParams = queryParams.set('autoPosterCreate', <any>autoPosterCreate);
    }
    const formData: FormData = new FormData();
    formData.append('file', file);

    return this.http.post<Program>(this.programApiUrl + `/${epgProgramId}/posters/${posterDefinitionId}/upload`, formData, {
      params: queryParams,
      responseType: 'text' as 'json',
    });
  }

  getProgramPoster(programId: string): Observable<ProgramPoster[]> {
    return this.http.get<ProgramPoster[]>(this.programApiUrl + `/${programId}/posters`);
  }

  removePoster(epgProgramId: string, posterId: string): Observable<any> {
    return this.http.delete<any>(this.programApiUrl + `/poster?programId=${epgProgramId}&posterId=${posterId}`, { responseType: 'text' as 'json' });
  }

  updateFrameProgramPoster(epgProgramId: string, file: File): Observable<any> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (epgProgramId) {
      queryParams = queryParams.set('epgProgramId', <any>epgProgramId);
    }
    let formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(this.programApiUrl + `/addinputpathtoprogram`, formData, { params: queryParams, responseType: 'text' as 'json' });
  }
  //
  setProgram(channelIds: number[], epgUpdateDate: string, day: number): Observable<ManualEpgImportResult> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });

    if (epgUpdateDate) {
      queryParams = queryParams.set('date', <any>epgUpdateDate);
      queryParams = queryParams.set('day', <any>day);
    }

    return this.http.post<ManualEpgImportResult>(this.programApiUrl + `/import/result`, channelIds, { params: queryParams });
  }
  deleteInputPath(inputPathId: number, epgProgramId: string): Observable<number> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (epgProgramId) {
      queryParams = queryParams.set('epgProgramId', <any>epgProgramId);
    }

    if (inputPathId) {
      queryParams = queryParams.set('inputPathId', <any>inputPathId);
    }
    return this.http.delete<number>(this.programApiUrl + `/deleteinputpathfromprogram`, { params: queryParams });
  }
  //Create Program
  createProgram(program: Program): Observable<Program> {
    return this.http.post<Program>(this.programApiUrl, program, { responseType: 'text' as 'json' });
  }

  getJobsByProgramId(epgProgramId: string): Observable<EpgCorrectionJob[]> {
    return this.http.get<EpgCorrectionJob[]>(this.programApiUrl + `/${epgProgramId}` + `/epgcorrection/job`);
  }

  getProgramStartEndTimes(epgProgramId: string): Observable<any> {
    return this.http.get<any>(`${this.programApiUrl}/${epgProgramId}`);
  }

  deleteProgram(epgProgramId: string): Observable<void> {
    return this.http.delete<any>(this.programApiUrl + '/' + epgProgramId);
  }

  isProgramExpired(epgProgramId: string): Observable<boolean> {
    return this.http.get<any>(this.programApiUrl + '/' + epgProgramId + '/isExpired');
  }

  getSeriesPosterByProgramId(epgProgramId: string): Observable<ProgramInputPath[]> {
    return this.http.get<ProgramInputPath[]>(this.programApiUrl + `/${epgProgramId}` + `/seriesPoster`);
  }

  getNeighborPrograms(epgProgramId: string): Observable<Program[]> {
    return this.http.get<Program[]>(this.programApiUrl + `/${epgProgramId}` + '/neighborPrograms');
  }
}
